package com.vamvisions.main.layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.vamvisions.main.core.domain.preferences.ColorScheme
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.i18n.languageLabel
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.navigation.AppLink
import com.vamvisions.main.theme.AppStyleSheet
import kotlin.js.Date
import kotlinx.browser.document
import org.jetbrains.compose.web.ExperimentalComposeWebSvgApi
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.jetbrains.compose.web.svg.Path
import org.jetbrains.compose.web.svg.Svg
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

private const val MAIN_ID = "main"
private const val NAV_LIST_ID = "main-nav-links"
private const val LANGUAGE_LIST_ID = "language-list"

/**
 * The frame of every page: skip link, header with navigation and toggles, main content and footer.
 * It sets the document title to `VAMVisions - <title>`.
 *
 * A page whose URL depends on the language passes [onLanguageSwitch]: after the interface language
 * changes, the route it returns replaces the current history entry. Without it, or when it returns
 * null, only the interface language changes.
 *
 * A [wide] page gets a wider main panel, for layouts with several columns. Without a [panel], the content sits
 * straight on the background, for pages made of glass cards.
 */
@Composable
fun PageLayout(
    title: String,
    chrome: PageChrome,
    onLanguageSwitch: ((Language) -> Route?)? = null,
    wide: Boolean = false,
    panel: Boolean = true,
    content: @Composable () -> Unit,
) {
    LaunchedEffect(title) {
        document.title = "VAMVisions - $title"
    }
    Div(attrs = { classes(LayoutStyles.page) }) {
        SkipLink()
        SiteHeader(chrome, onLanguageSwitch)
        Main(attrs = {
            id(MAIN_ID)
            attr("tabindex", "-1")
            classes(if (panel) LayoutStyles.main else LayoutStyles.mainBare, AppStyleSheet.container)
            if (wide) classes(LayoutStyles.mainWide)
        }) {
            content()
        }
        SiteFooter()
    }
}

@Composable
private fun SkipLink() {
    A(href = "#$MAIN_ID", attrs = { classes(LayoutStyles.skipLink) }) {
        Text(LocalStrings.current.skipToContent)
    }
}

/** The header menus. At most one is open at a time, so opening one closes the other. */
private enum class HeaderMenu { NAVIGATION, LANGUAGE }

@Composable
private fun SiteHeader(chrome: PageChrome, onLanguageSwitch: ((Language) -> Route?)?) {
    var openMenu by remember { mutableStateOf<HeaderMenu?>(null) }
    LaunchedEffect(chrome.currentRoute) { openMenu = null }
    // Closes [menu] only while it is the open one, so a click that opens the other menu isn't undone.
    val close: (HeaderMenu) -> Unit = { menu -> if (openMenu == menu) openMenu = null }
    val toggle: (HeaderMenu) -> Unit = { menu -> openMenu = if (openMenu == menu) null else menu }

    Header(attrs = { classes(LayoutStyles.header) }) {
        Div(attrs = { classes(LayoutStyles.headerRow, AppStyleSheet.container) }) {
            AppLink(Route.Home, chrome.onNavigate, attrs = { classes(LayoutStyles.brand) }) {
                Text("VAMVisions")
            }
            MainNavigation(
                chrome = chrome,
                isOpen = openMenu == HeaderMenu.NAVIGATION,
                onToggle = { toggle(HeaderMenu.NAVIGATION) },
                onClose = { close(HeaderMenu.NAVIGATION) },
            )
            Div(attrs = { classes(LayoutStyles.toggles) }) {
                ColorSchemeToggle(chrome.colorScheme, chrome.onToggleColorScheme)
                LanguageMenu(
                    language = chrome.language,
                    isOpen = openMenu == HeaderMenu.LANGUAGE,
                    onToggle = { toggle(HeaderMenu.LANGUAGE) },
                    onClose = { close(HeaderMenu.LANGUAGE) },
                    onLanguageChange = { language ->
                        chrome.onLanguageChange(language)
                        onLanguageSwitch?.invoke(language)?.let(chrome.onReplace)
                    },
                )
            }
        }
    }
}

/**
 * While [isOpen], closes a header menu on Escape, returning focus to its [button], and on a click outside its
 * [container].
 */
@Composable
private fun CloseOnEscapeOrOutsideClick(isOpen: Boolean, container: ElementHolder, button: ElementHolder, onClose: () -> Unit) {
    if (!isOpen) return
    DisposableEffect(Unit) {
        val onKeyDown: (Event) -> Unit = { event ->
            if ((event as KeyboardEvent).key == "Escape") {
                onClose()
                button.element?.focus()
            }
        }
        val onClick: (Event) -> Unit = { event ->
            if (container.element?.contains(event.target as? Node) != true) onClose()
        }
        document.addEventListener("keydown", onKeyDown)
        document.addEventListener("click", onClick)
        onDispose {
            document.removeEventListener("keydown", onKeyDown)
            document.removeEventListener("click", onClick)
        }
    }
}

/**
 * The page links. On narrow screens they sit behind a menu button, which closes on navigation, on Escape and on a
 * click outside it; Escape also returns focus to the button.
 */
@Composable
private fun MainNavigation(chrome: PageChrome, isOpen: Boolean, onToggle: () -> Unit, onClose: () -> Unit) {
    val strings = LocalStrings.current
    val nav = remember { ElementHolder() }
    val menuButton = remember { ElementHolder() }
    CloseOnEscapeOrOutsideClick(isOpen, nav, menuButton, onClose)

    val navigateAndClose: (Route) -> Unit = { route ->
        onClose()
        chrome.onNavigate(route)
    }
    Nav(attrs = {
        classes(LayoutStyles.nav)
        attr("aria-label", strings.mainNavigationLabel)
        ref { element ->
            nav.element = element
            onDispose { nav.element = null }
        }
    }) {
        Button(attrs = {
            type(ButtonType.Button)
            classes(LayoutStyles.toggleButton, LayoutStyles.menuButton)
            attr("aria-controls", NAV_LIST_ID)
            attr("aria-expanded", isOpen.toString())
            attr("aria-label", if (isOpen) strings.closeMenu else strings.openMenu)
            ref { element ->
                menuButton.element = element
                onDispose { menuButton.element = null }
            }
            onClick { onToggle() }
        }) {
            Span(attrs = { attr("aria-hidden", "true") }) { Text("☰") }
        }
        Ul(attrs = {
            id(NAV_LIST_ID)
            classes(LayoutStyles.navList)
            if (isOpen) classes(LayoutStyles.navListOpen)
        }) {
            NavItem(Route.Home, strings.navHome, chrome.currentRoute, navigateAndClose)
            NavItem(Route.Portfolio, strings.navPortfolio, chrome.currentRoute, navigateAndClose)
            NavItem(Route.Projects, strings.navProjects, chrome.currentRoute, navigateAndClose)
            NavItem(Route.Contact, strings.navContact, chrome.currentRoute, navigateAndClose)
        }
    }
}

/** Holds a DOM element captured with `ref`, without triggering recomposition. */
private class ElementHolder {
    var element: HTMLElement? = null
}

@Composable
private fun NavItem(route: Route, label: String, currentRoute: Route, onNavigate: (Route) -> Unit) {
    Li {
        AppLink(
            route = route,
            onNavigate = onNavigate,
            attrs = {
                classes(LayoutStyles.navLink)
                if (currentRoute == route) attr("aria-current", "page")
            },
        ) {
            Text(label)
        }
    }
}

@Composable
private fun ColorSchemeToggle(colorScheme: ColorScheme, onToggle: () -> Unit) {
    val strings = LocalStrings.current
    Button(attrs = {
        type(ButtonType.Button)
        classes(LayoutStyles.toggleButton)
        attr(
            "aria-label",
            when (colorScheme) {
                ColorScheme.LIGHT -> strings.switchToDarkScheme
                ColorScheme.DARK -> strings.switchToLightScheme
            },
        )
        onClick { onToggle() }
    }) {
        Span(attrs = { attr("aria-hidden", "true") }) {
            Text(
                when (colorScheme) {
                    ColorScheme.LIGHT -> "☾"
                    ColorScheme.DARK -> "☀"
                }
            )
        }
    }
}

/**
 * The language switch: a button showing the active language's code, which opens a list of every language under its
 * own name. Choosing one closes the list and returns focus to the button; it also closes on Escape and on a click
 * outside it.
 */
@Composable
private fun LanguageMenu(
    language: Language,
    isOpen: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit,
    onLanguageChange: (Language) -> Unit,
) {
    val strings = LocalStrings.current
    val menu = remember { ElementHolder() }
    val menuButton = remember { ElementHolder() }
    CloseOnEscapeOrOutsideClick(isOpen, menu, menuButton, onClose)

    Div(attrs = {
        classes(LayoutStyles.languageMenu)
        ref { element ->
            menu.element = element
            onDispose { menu.element = null }
        }
    }) {
        Button(attrs = {
            type(ButtonType.Button)
            classes(LayoutStyles.toggleButton)
            attr("aria-controls", LANGUAGE_LIST_ID)
            attr("aria-expanded", isOpen.toString())
            // The accessible name starts with the visible text, so voice control users can say it.
            attr("aria-label", "${languageLabel(language)} – ${strings.changeLanguage}")
            ref { element ->
                menuButton.element = element
                onDispose { menuButton.element = null }
            }
            onClick { onToggle() }
        }) {
            Text(languageLabel(language))
            ChevronIcon(isOpen)
        }
        Ul(attrs = {
            id(LANGUAGE_LIST_ID)
            classes(LayoutStyles.languageList)
            if (isOpen) classes(LayoutStyles.languageListOpen)
        }) {
            Language.entries.forEach { option ->
                Li {
                    Button(attrs = {
                        type(ButtonType.Button)
                        classes(LayoutStyles.languageOption)
                        attr("lang", option.code)
                        if (option == language) attr("aria-current", "true")
                        onClick {
                            onClose()
                            menuButton.element?.focus()
                            if (option != language) onLanguageChange(option)
                        }
                    }) {
                        Text(option.nativeName)
                    }
                }
            }
        }
    }
}

/** A downward chevron, drawn in the button's text colour. It turns upward while [isOpen]. */
@OptIn(ExperimentalComposeWebSvgApi::class)
@Composable
private fun ChevronIcon(isOpen: Boolean) {
    Svg(viewBox = "0 0 24 24", attrs = {
        classes(LayoutStyles.chevron)
        if (isOpen) classes(LayoutStyles.chevronOpen)
        attr("fill", "none")
        attr("stroke", "currentColor")
        attr("stroke-width", "2.5")
        attr("stroke-linecap", "round")
        attr("stroke-linejoin", "round")
        attr("aria-hidden", "true")
        attr("focusable", "false")
    }) {
        Path("M6 9l6 6 6-6")
    }
}

@Composable
private fun SiteFooter() {
    Footer(attrs = { classes(LayoutStyles.footer) }) {
        Div(attrs = { classes(AppStyleSheet.container) }) {
            Text("© ${Date().getFullYear()} Vilcu Ana-Maria")
        }
    }
}
